import React, { useState, useEffect, useMemo } from 'react';
import safeToast from '../../utils/safeToast';
import { getCurrentUser } from '../../api/auth';
import {
  listManagers,
  createManager as apiCreateManager,
  updateManager as apiUpdateManager,
  deleteManager as apiDeleteManager,
  toggleManagerActive as apiToggleManagerActive,
  MANAGER_TASK_LABELS,
  type ManagerRecord,
  type ManagerTask,
} from '../../api/managers';
import {
  Users as UsersIcon,
  Plus,
  Search as SearchIcon,
  Shield,
  Mail,
  Phone as PhoneIcon,
  Crown,
  Edit as EditIcon,
  Trash2,
  X,
  Check,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { searchUsers as apiSearchUsers } from '../../api/adminUsers';

// Redesigned Managers System Control panel
export default function SystemAdminManagersPanel({ onBackToAdmin }: { onBackToAdmin?: () => void }) {
  const [managers, setManagers] = useState<ManagerRecord[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [myRole, setMyRole] = useState<string | null>(null);
  const canManage = useMemo(() => String(myRole || '').toLowerCase() === 'superadmin', [myRole]);

  // managers filters/search/sort
  const [mgrQuery, setMgrQuery] = useState('');
  const [mgrRole, setMgrRole] = useState(''); // '', 'basicadmin', 'superadmin'
  const [mgrStatus, setMgrStatus] = useState(''); // '', 'active', 'inactive'

  // activity logs filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterActor, setFilterActor] = useState<string>('');
  const [filterAction, setFilterAction] = useState<string>('');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  // create/edit
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', gender: '', email: '', phone: '', role: 'basicadmin', password: '', assignedTasks: [] as ManagerTask[] });
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [editing, setEditing] = useState<ManagerRecord & { password?: string } | null>(null);
  const [showEditingPassword, setShowEditingPassword] = useState(false);

  // Promote-from-users modal state
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteQuery, setPromoteQuery] = useState('');
  const [promoteResults, setPromoteResults] = useState<any[]>([]);
  const [promoteRole, setPromoteRole] = useState<'basicadmin' | 'superadmin'>('basicadmin');
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    // load current user role then managers from server
    (async () => {
      try {
        const me = await getCurrentUser();
        const role = (me as any)?.adminRole || 'superadmin';
        setMyRole(role);
        const data = await listManagers();
        setManagers((data || []).map(m => ({ ...m })));
      } catch (err) {
        console.error('Failed to load managers', err);
        safeToast('error', 'Please login as admin to manage managers');
      }
    })();
    // load basic admin activity logs for Super Admin review
    try {
      const raw = localStorage.getItem('basic_admin_activity');
      const parsed = raw ? JSON.parse(raw) : [];
      setLogs(parsed || []);
    } catch (e) {
      setLogs([]);
    }
  }, []);

  const createManager = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      safeToast('error', 'Name and email are required');
      return;
    }
    try {
      const created: any = await apiCreateManager(form);
      setManagers(s => [created as ManagerRecord, ...s]);
      if ((created as any)?.promoted) safeToast('success', 'Existing card user upgraded to Basic Admin');
      else safeToast('success', 'Basic admin created');
      setForm({ name: '', gender: '', email: '', phone: '', role: 'basicadmin', password: '', assignedTasks: [] });
      setShowCreate(false);
    } catch (err: any) {
      console.error(err);
      const message = (err && (err.data?.error || err.message)) || 'Failed to create manager';
      safeToast('error', message);
    }
  };

  const openManager = (m: ManagerRecord) => {
    setEditing({ ...m });
    setShowEditingPassword(false);
  };

  const saveEditing = () => {
    if (!editing) return;
    (async () => {
      try {
        const { password, ...payload } = editing as any;
        const updated = await apiUpdateManager(editing.id, editing);
        setManagers(s => s.map(x => x.id === (updated as any).id ? (updated as ManagerRecord) : x));
        setEditing(null);
        setShowEditingPassword(false);
        safeToast('success', 'Manager updated');
      } catch (err) {
        console.error(err);
        safeToast('error', 'Failed to update manager');
      }
    })();
  };

  const filteredManagers = useMemo(() => {
    const q = mgrQuery.trim().toLowerCase();
    return managers.filter((m) => {
      const matchesQuery = !q || Object.values(m).join(' ').toLowerCase().includes(q);
      const matchesRole = !mgrRole || (m.role || '').toLowerCase() === mgrRole.toLowerCase();
      const matchesStatus = !mgrStatus || ((m.isActive ?? true) === (mgrStatus === 'active'));
      return matchesQuery && matchesRole && matchesStatus;
    });
  }, [managers, mgrQuery, mgrRole, mgrStatus]);

  // Approvals management removed from Managers panel; use Bulk & Approvals tab instead.

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <UsersIcon className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 shrink-0" />
          <div className="min-w-0">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Managers — System Control</h2>
            <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Create, review, and manage Basic Admins and Super Admins</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {canManage && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Manager</span>
              <span className="sm:hidden">Add</span>
            </button>
          )}
          {canManage && (
            <button
              onClick={() => setPromoteOpen(true)}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-900 transition-colors"
            >
              <SearchIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Promote Existing User</span>
              <span className="sm:hidden">Promote</span>
            </button>
          )}
          <button
            onClick={() => onBackToAdmin && onBackToAdmin()}
            className="px-3 sm:px-4 py-2 bg-gray-100 text-gray-800 text-sm rounded-lg hover:bg-gray-200 transition-colors"
          >
            <span className="hidden sm:inline">Back to Admin</span>
            <span className="sm:hidden">Back</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-6 space-y-6">
        {/* Managers card */}
        <div className="border border-gray-200 rounded-lg">
          <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2 text-gray-700">
              <Shield className="w-4 h-4 text-red-600" />
              <span className="text-sm">{managers.length} total • {managers.filter(m => m.isActive ?? true).length} active</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <div className="relative flex-1 min-w-0 sm:min-w-[220px]">
                <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  placeholder="Search managers"
                  value={mgrQuery}
                  onChange={(e) => setMgrQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <select
                value={mgrRole}
                onChange={(e) => setMgrRole(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">All roles</option>
                <option value="basicadmin">Basic Admin</option>
                <option value="superadmin">Super Admin</option>
              </select>
              <select
                value={mgrStatus}
                onChange={(e) => setMgrStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">All status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <button
                onClick={() => { setMgrQuery(''); setMgrRole(''); setMgrStatus(''); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Phone</th>
                  <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Assigned Tasks</th>
                  <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-2 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredManagers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      No managers found. Try adjusting filters or add a new manager.
                    </td>
                  </tr>
                )}
                {filteredManagers.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {m.role === 'superadmin' ? (
                          <Crown className="w-4 h-4 text-amber-500" />
                        ) : (
                          <UsersIcon className="w-4 h-4 text-gray-400" />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{m.name}</div>
                          <div className="text-xs text-gray-500">ID: {m.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {m.email}
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-700 hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        <PhoneIcon className="w-4 h-4 text-gray-400" />
                        {m.phone || '—'}
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <span
                        className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                          (m.role || '') === 'superadmin'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {(m.role || 'basicadmin').replace(/^(.)/, (c) => c.toUpperCase())}
                      </span>
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {m.role === 'superadmin' ? (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200">All Access</span>
                        ) : (m.assignedTasks && m.assignedTasks.length > 0) ? (
                          m.assignedTasks.map(t => (
                            <span key={t} className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                              {MANAGER_TASK_LABELS[t] || t}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">No tasks</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <button
                        onClick={async () => {
                          if (!canManage) return;
                          try {
                            const newActive = !(m.isActive ?? true);
                            await apiToggleManagerActive(m.id, newActive);
                            setManagers((s) => s.map(x => (x.id === m.id ? { ...x, isActive: newActive } : x)));
                            safeToast('success', newActive ? 'Activated' : 'Deactivated');
                          } catch (e: any) {
                            safeToast('error', e?.message || 'Failed to update status');
                          }
                        }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                        title={(m.isActive ?? true) ? 'Deactivate' : 'Activate'}
                        disabled={!canManage || m.role === 'superadmin'}
                      >
                        {(m.isActive ?? true) ? (
                          <>
                            <ToggleRight className="w-4 h-4 text-green-600" />
                            <span className="text-gray-700">Active</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700">Inactive</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                          onClick={() => { if (!canManage) return; openManager(m); }}
                          title="View / Edit"
                          disabled={!canManage}
                        >
                          <EditIcon className="w-4 h-4" /> Edit
                        </button>
                        <button
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 ${m.role === 'superadmin' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={m.role === 'superadmin' || !canManage}
                          title={m.role === 'superadmin' ? 'Cannot remove superadmin' : 'Downgrade to Card Owner'}
                          onClick={async () => {
                            if (m.role === 'superadmin' || !canManage) return;
                            try {
                              await apiDeleteManager(m.id);
                              setManagers(s => s.filter(x => x.id !== m.id));
                              safeToast('success', 'Manager downgraded to Card Owner');
                            } catch (err: any) {
                              console.error(err);
                              const message = (err && (err.data?.error || err.message)) || 'Failed to remove manager';
                              safeToast('error', message);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" /> Downgrade
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity Logs */}
        <div className="border border-gray-200 rounded-lg">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UsersIcon className="w-4 h-4 text-red-600" />
              <h3 className="font-semibold">Basic Admin Activity</h3>
            </div>
            <div className="text-xs text-gray-600">Showing {logs.length} logs</div>
          </div>

          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-0 sm:min-w-[220px]">
                <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  placeholder="Search logs"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <select
                value={filterActor}
                onChange={e => setFilterActor(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 min-w-0 sm:min-w-[160px]"
              >
                <option value="">All actors</option>
                {[...new Set(logs.map(l => String(l.admin || l.actor || l.actorEmail || '')))].filter(Boolean).map((a: string) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select
                value={filterAction}
                onChange={e => setFilterAction(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 min-w-0 sm:min-w-[160px]"
              >
                <option value="">All actions</option>
                {[...new Set(logs.map(l => String(l.action || l.type || l.activity || '')))].filter(Boolean).map((a: string) => <option key={a} value={a}>{a}</option>)}
              </select>
              <div className="flex flex-col min-w-0 sm:min-w-[150px]">
                <label className="text-xs text-gray-600 mb-1">From</label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={e => setFilterStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex flex-col min-w-0 sm:min-w-[150px]">
                <label className="text-xs text-gray-600 mb-1">To</label>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={e => setFilterEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <button
                onClick={() => { setSearchTerm(''); setFilterActor(''); setFilterAction(''); setFilterStartDate(''); setFilterEndDate(''); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 shrink-0"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="p-4">
            {logs.length === 0 ? (
              <p className="text-sm text-gray-500">No logs yet.</p>
            ) : (
              <>
                <div className="max-h-[40vh] overflow-auto rounded border border-gray-200">
                  <ul className="divide-y divide-gray-200">
                    {logs
                      .filter(l => {
                        const text = (JSON.stringify(l) || '').toLowerCase();
                        if (searchTerm && !text.includes(searchTerm.toLowerCase())) return false;
                        if (filterActor) {
                          const a = String(l.admin || l.actor || l.actorEmail || '').toLowerCase();
                          if (!a.includes(filterActor.toLowerCase())) return false;
                        }
                        if (filterAction) {
                          const act = String(l.action || l.type || l.activity || '').toLowerCase();
                          if (!act.includes(filterAction.toLowerCase())) return false;
                        }
                        if (filterStartDate) {
                          const d = new Date(l.at || l.createdAt || l.date || null);
                          if (isNaN(d.getTime())) return false;
                          const sd = new Date(filterStartDate + 'T00:00:00');
                          if (d < sd) return false;
                        }
                        if (filterEndDate) {
                          const d = new Date(l.at || l.createdAt || l.date || null);
                          if (isNaN(d.getTime())) return false;
                          const ed = new Date(filterEndDate + 'T23:59:59');
                          if (d > ed) return false;
                        }
                        return true;
                      })
                      .map((l: any) => (
                        <li key={l.id || l.at || Math.random()} className="p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{l.admin || l.actor || l.actorEmail || 'BasicAdmin'}</div>
                              <div className="text-xs text-gray-600">{l.action || l.activity || l.text || ''}</div>
                            </div>
                            <div className="text-xs text-gray-500">{new Date(l.at || l.createdAt || l.date || Date.now()).toLocaleString()}</div>
                          </div>
                        </li>
                      ))}
                  </ul>
                </div>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button onClick={() => { try { navigator.clipboard?.writeText(JSON.stringify(logs, null, 2)); safeToast('success', 'Activity copied to clipboard'); } catch {} }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Copy Log</button>
                  <button onClick={() => { const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `basic-admin-activities-${Date.now()}.json`; a.click(); URL.revokeObjectURL(url); }} className="px-3 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-900">Download Log</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Manager Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-xl rounded-lg shadow-lg">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold">Create Basic Admin</h3>
              <button className="p-2 hover:bg-gray-100 rounded" onClick={() => setShowCreate(false)} aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Name</label>
                  <input name="manager-create-name" autoComplete="off" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" className="w-full p-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Gender</label>
                  <input name="manager-create-gender" autoComplete="off" value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))} placeholder="Gender" className="w-full p-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Email</label>
                  <input name="manager-create-email" autoComplete="off" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="w-full p-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Phone</label>
                  <input name="manager-create-phone" autoComplete="off" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" className="w-full p-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700">Login Password</label>
                  <div className="flex gap-2">
                    <input
                      name="manager-create-password"
                      autoComplete="new-password"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      placeholder="Password (set login password)"
                      type={showCreatePassword ? 'text' : 'password'}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                    <button type="button" onClick={() => setShowCreatePassword(s => !s)} className="px-3 py-2 border border-gray-300 rounded-lg min-w-[84px]">
                      {showCreatePassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
              </div>
              {/* Task Assignment */}
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">Assign Tasks</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(Object.entries(MANAGER_TASK_LABELS) as [ManagerTask, string][]).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.assignedTasks.includes(key)}
                        onChange={e => {
                          setForm(f => ({
                            ...f,
                            assignedTasks: e.target.checked
                              ? [...f.assignedTasks, key]
                              : f.assignedTasks.filter(t => t !== key),
                          }));
                        }}
                        className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mt-4 justify-end">
                <button onClick={() => setForm({ name: '', gender: '', email: '', phone: '', role: 'basicadmin', password: '', assignedTasks: [] })} className="px-4 py-2 border border-gray-300 rounded-lg">Reset</button>
                <button onClick={createManager} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Manager Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-xl rounded-lg shadow-lg">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold">Edit Manager</h3>
              <button className="p-2 hover:bg-gray-100 rounded" onClick={() => setEditing(null)} aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Name</label>
                  <input name="manager-edit-name" autoComplete="off" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Email</label>
                  <input name="manager-edit-email" autoComplete="off" value={editing.email} onChange={e => setEditing({ ...editing, email: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Role</label>
                  <select name="manager-edit-role" value={editing.role || 'basicadmin'} onChange={e => setEditing({ ...editing, role: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg">
                    <option value="basicadmin">Basic Admin</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Phone</label>
                  <input name="manager-edit-phone" autoComplete="off" value={editing.phone || ''} onChange={e => setEditing({ ...editing, phone: e.target.value })} className="w-full p-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700">Login Password</label>
                  <div className="flex gap-2">
                    <input name="manager-edit-password" autoComplete="new-password" value={(editing as any).password || ''} onChange={e => setEditing({ ...editing, password: e.target.value })} placeholder="Change password (optional)" type={showEditingPassword ? 'text' : 'password'} className="w-full p-2 border border-gray-300 rounded-lg" />
                    <button type="button" onClick={() => setShowEditingPassword(s => !s)} className="px-3 py-2 border border-gray-300 rounded-lg">{showEditingPassword ? 'Hide' : 'Show'}</button>
                  </div>
                </div>
              </div>
              {/* Task Assignment (Edit) */}
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">Assigned Tasks</label>
                {editing.role === 'superadmin' ? (
                  <p className="text-sm text-amber-600">Super Admins have access to all tasks automatically.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(Object.entries(MANAGER_TASK_LABELS) as [ManagerTask, string][]).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(editing.assignedTasks || []).includes(key)}
                          onChange={e => {
                            const current = editing.assignedTasks || [];
                            setEditing({
                              ...editing,
                              assignedTasks: e.target.checked
                                ? [...current, key]
                                : current.filter(t => t !== key),
                            });
                          }}
                          className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-4 justify-end">
                <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
                <button onClick={saveEditing} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promote Existing User Modal */}
      {promoteOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold">Promote Existing User to Manager</h3>
              <button className="p-2 hover:bg-gray-100 rounded" onClick={() => setPromoteOpen(false)} aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    placeholder="Search by name, email, username, handle, company, position"
                    value={promoteQuery}
                    onChange={e => setPromoteQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <select value={promoteRole} onChange={e => setPromoteRole(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="basicadmin">Basic Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
                <button
                  onClick={async () => {
                    const q = promoteQuery.trim();
                    if (!q) return;
                    setIsSearching(true);
                    try {
                      const items = await apiSearchUsers(q, 20);
                      setPromoteResults(items);
                    } catch (e:any) {
                      safeToast('error', e?.message || 'Search failed');
                    } finally {
                      setIsSearching(false);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  {isSearching ? 'Searching…' : 'Search'}
                </button>
              </div>
              <div className="border border-gray-200 rounded-lg max-h-[50vh] overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {promoteResults.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">{isSearching ? 'Searching…' : 'No results yet.'}</td>
                      </tr>
                    )}
                    {promoteResults.map((u:any) => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <div className="font-medium text-gray-900">{u.name || '—'}</div>
                          <div className="text-xs text-gray-500">@{u.username} · {u.handle}</div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">{u.email}</td>
                        <td className="px-4 py-2 text-sm">
                          {u.isManager ? (
                            <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">Already a manager</span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">Card Owner</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            disabled={u.isManager}
                            onClick={async () => {
                              try {
                                const res = await apiCreateManager({ name: u.name || u.username || 'Admin', email: u.email, role: promoteRole });
                                setManagers(s => [res as any, ...s]);
                                setPromoteOpen(false);
                                safeToast('success', `Promoted ${u.name || u.email} to ${promoteRole === 'superadmin' ? 'Super Admin' : 'Basic Admin'}`);
                              } catch (e:any) {
                                safeToast('error', e?.data?.error || e?.message || 'Failed to promote');
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm ${u.isManager ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-slate-800 text-white hover:bg-slate-900'}`}
                          >
                            Promote
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPromoteOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
